/**
 * MessageChannel values for support-v2.
 * These match the `MessageChannel` type from `effect-api/shared` but are hardcoded
 * here to avoid a circular dependency (effect-api/shared → redo/model, so redo/model
 * cannot import from effect-api/shared).
 */
export const MESSAGE_CHANNELS_V3 = [
  "gmail",
  "outlook",
  "customEmail",
  "redoChat",
  "facebook",
  "facebookComments",
  "instagram",
  "instagramComments",
  "sms",
  "voice",
  "attentive",
  "postscript",
  "tiktok",
  "contactForm",
] as const;

export type MessageChannelV3 = (typeof MESSAGE_CHANNELS_V3)[number];

export const MESSAGE_TYPE_CHANNELS_V3 = [
  ...MESSAGE_CHANNELS_V3,
  "internalNote",
] as const;

export type MessageTypeChannelV3 = (typeof MESSAGE_TYPE_CHANNELS_V3)[number];

/**
 * Message sender types that can scope a Rules V3 message-count condition.
 * These match the communication message sender values without introducing a
 * dependency on the communication model.
 */
export const MESSAGE_COUNT_SENDER_TYPES_V3 = [
  "customer",
  "merchant",
  "ai",
] as const;

export type MessageCountSenderTypeV3 =
  (typeof MESSAGE_COUNT_SENDER_TYPES_V3)[number];

/**
 * ConversationStatus values for support-v2.
 * These match the `ConversationStatus` type from `effect-api/shared`.
 * V3 supports more statuses than v2 (adds HIDDEN, ARCHIVED, DELETED).
 */
export const CONVERSATION_STATUSES_V3 = [
  "OPEN",
  "CLOSED",
  "SNOOZED",
  "IN_PROGRESS",
  "HIDDEN",
  "ARCHIVED",
  "DELETED",
] as const;

export type ConversationStatusV3 = (typeof CONVERSATION_STATUSES_V3)[number];

/**
 * Per-thread AI engagement statuses. Source of truth for the DB column
 * (`ai_status` enum in
 * `cockroachdb/schema/definitions/tables/shopper-ai/conversation/thread/product-data/ai-status-enum.sql`)
 * and for the rules-v3 `AI_STATUS` condition. `effect-api/shared`
 * re-exports this constant and wraps it in an effect `Schema`; the rules
 * engine cannot import from `effect-api/shared` (circular), so the
 * canonical declaration lives here in `redo/model`.
 */
export const AI_STATUSES = ["ACTIVE", "HANDED_OFF"] as const;
export type AiStatus = (typeof AI_STATUSES)[number];

export const SHOPPER_AGENT_OUTREACH_CONDITION_VALUES = [
  "none",
  "recover",
  "sms",
  "shopper_agent",
  "campaign",
  "outreach_agent",
] as const;

export type ShopperAgentOutreachConditionValue =
  (typeof SHOPPER_AGENT_OUTREACH_CONDITION_VALUES)[number];

export const SHOPPER_AGENT_OUTREACH_CONDITION_LABELS: Record<
  ShopperAgentOutreachConditionValue,
  string
> = {
  none: "None",
  recover: "Recover",
  sms: "SMS",
  shopper_agent: "Commerce Agent",
  campaign: "Campaign",
  outreach_agent: "Outreach agent",
};

export function shopperAgentOutreachConditionLabel(
  value: string,
): string | undefined {
  switch (value) {
    case "none":
    case "recover":
    case "sms":
    case "shopper_agent":
    case "campaign":
    case "outreach_agent":
      return SHOPPER_AGENT_OUTREACH_CONDITION_LABELS[value];
    default:
      return undefined;
  }
}

/**
 * Time units for rules-v3 conditions and actions that take a `value + unit`
 * duration (response time, last-order time, merge-support-ticket age window).
 *
 * Defined here in `types.ts` rather than `schemas.ts` because `rules/actions.ts`
 * also needs the type and importing `schemas.ts` would create a cycle
 * (`rules/schemas → rules/actions → rules-v3/schemas → rules/schemas`).
 */
export enum TimeUnit {
  MINUTES = "minutes",
  HOURS = "hours",
  DAYS = "days",
}
